import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ServiceBenefitsSlider } from '@/components/ServiceBenefitsSlider';
import { ServiceDetailsSlider } from '@/components/ServiceDetailsSlider';
import { Helmet } from 'react-helmet-async';

export default function ServiceTemplate({ title, subtitle, description, heroImage, infoImage, heroBackgroundImage, benefits, details, faqs, onOpenPopup, testId, metaTitle, metaDescription }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" data-testid={testId}>
      <Helmet>
        <title>{metaTitle || `${title} - Trademark Venus`}</title>
        <meta name="description" content={metaDescription || description} />
      </Helmet>
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          {heroBackgroundImage && (
            <img 
              src={heroBackgroundImage}
              alt="Hero background"
              className="w-full h-full object-cover opacity-20"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 opacity-90" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-red-400 mb-4 block">
                {subtitle}
              </span>
              <h1 className="text-5xl md:text-6xl font-heading font-bold text-white leading-tight mb-6">
                {title}
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                {description}
              </p>
              <Button
                onClick={onOpenPopup}
                data-testid="hero-service-button"
                className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-xl transition-all duration-300"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-2xl border border-slate-200/20">
                <img
                  src={heroImage}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info Section - Image Right, Content Left */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-red-700 mb-4 block">
                Our Expertise
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
                Professional {title} Services
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {description}
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Our experienced team understands the complexities involved in protecting your intellectual property. We work diligently to ensure your application is prepared accurately, filed promptly, and monitored throughout the entire process.
              </p>
              <Button
                onClick={onOpenPopup}
                data-testid="info-section-button"
                className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white font-semibold rounded-full px-8 py-4 shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-xl border border-slate-200">
                <img
                  src={infoImage}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      {benefits && benefits.length > 0 && (
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
                Key Benefits
              </h2>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                Why choose our services for your business needs
              </p>
            </motion.div>

            <ServiceBenefitsSlider benefits={benefits} />
          </div>
        </section>
      )}

      {/* Detailed Info Section */}
      {details && details.length > 0 && (
        <section className="py-12 md:py-20 bg-gradient-to-b from-white to-slate-50">
          <div className="container mx-auto px-4 md:px-6">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-red-700 mb-4 block">
                Complete Package
              </span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
                What You Get With Our Service
              </h2>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                Comprehensive support and professional guidance throughout your journey
              </p>
            </motion.div>

            <ServiceDetailsSlider details={details} />
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && (
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                Get answers to common questions
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
                >
                  <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-red-900 via-red-700 to-red-600">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-red-100 text-lg max-w-2xl mx-auto mb-8">
              Contact us today to learn more about how we can help protect your business.
            </p>
            <Button
              onClick={onOpenPopup}
              data-testid="cta-service-button"
              className="bg-white text-red-700 hover:bg-red-50 font-semibold rounded-full px-8 py-6 text-lg shadow-xl transition-all duration-300"
            >
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
